import { httpClient } from '../client';
import {
  ListBucketResponse,
  PresignedUrlResponse,
  GenerateUrlRequest,
  KnowledgeBaseUrls,
  IngestResponse,
  CrawlStatusResponse,
  ParsedCrawlStatus,
} from '../../../types/file';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';

class FileEndpoints extends BaseEndpoint {
  private static instance: FileEndpoints;

  private constructor() {
    super();
  }

  static getInstance(): FileEndpoints {
    if (!FileEndpoints.instance) {
      FileEndpoints.instance = new FileEndpoints();
    }
    return FileEndpoints.instance;
  }

  async listFiles(clientId: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<ListBucketResponse>(
        `${this.basePath}/listbucket`,
        { clientId },
        { headers }
      );

      // Transform the response to match our FileItem interface
      const files = response.data.Contents.map(item => ({
        id: item.ETag.replace(/"/g, ''),
        name: item.Key.split('/').pop() || '',
        size: item.Size,
        type: item.Key.split('.').pop()?.toUpperCase() || '',
        lastModified: item.LastModified,
        key: item.Key,
      }));

      logger.info('Files listed successfully');
      return files;
    } catch (error) {
      logger.error('Failed to list files:', error);
      throw error;
    }
  }

  async generatePresignedUrl(data: GenerateUrlRequest) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<PresignedUrlResponse>(
        `${this.basePath}/generatepresignedurl`,
        data,
        { headers }
      );

      logger.info('Presigned URL generated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to generate presigned URL:', error);
      throw error;
    }
  }

  async uploadFileWithPresignedUrl(presignedUrl: string, file: File) {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      logger.info('File uploaded successfully' + response);
      return true;
    } catch (error) {
      logger.error('Failed to upload file:', error);
      throw error;
    }
  }

  // inside FileEndpoints class
  async downloadFile(clientId: string, fileName: string): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();
      // GET /download?clientId=xxx&fileName=yyy
      const response = await httpClient.get<{ downloadUrl: string }>(`${this.basePath}/download`, {
        headers,
        params: { clientId, fileName },
      });
      return response.data.downloadUrl;
    } catch (error) {
      logger.error('Failed to obtain download URL:', error);
      throw error;
    }
  }

  async deleteFile(key: string) {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.basePath}/removefile/${key}`, {
        headers,
      });

      logger.info('File deleted successfully');
      return true;
    } catch (error) {
      logger.error('Failed to delete file:', error);
      throw error;
    }
  }

  async ingestFiles(clientId: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post(`${this.basePath}/ingest`, { clientId }, { headers });

      logger.info('Files ingested successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to ingest files:', error);
      throw error;
    }
  }
  async ingestWebFiles(clientId: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post(
        `${this.basePath}/ingest/web`,
        { clientId },
        { headers }
      );

      logger.info('Files ingested successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to ingest files:', error);
      throw error;
    }
  }

  async createKnowledgeBaseUrls(data: KnowledgeBaseUrls): Promise<IngestResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<IngestResponse>(
        `${this.basePath}/ingest/web`,
        { ...data },
        {
          headers,
        }
      );
      logger.info('Knowledge base Urls created successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to create knowledge base Urls:', error);
      throw error;
    }
  }

  async getKnowledgeBaseUrls(clientId: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get(`${this.basePath}/ingest/${clientId}/web`, { headers });
      logger.info('Knowledge base Urls fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch knowledge base Urls:', error);
      throw error;
    }
  }

  async deleteKnowledgeBaseUrls(clientId: string, url?: string) {
    try {
      const headers = await this.getAuthHeaders();
      const endpoint = `${this.basePath}/ingest/${clientId}/web`;

      // If URL is provided, send it in the request body for single URL deletion
      // Otherwise, delete all URLs
      const config = url
        ? {
            headers,
            data: { url },
          }
        : { headers };

      const response = await httpClient.delete(endpoint, config);
      logger.info('Knowledge base Urls deleted successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to delete knowledge base Urls:', error);
      throw error;
    }
  }

  async getCrawlStatus(clientId: string, uuid: string): Promise<ParsedCrawlStatus> {
    try {
      const headers = await this.getAuthHeaders();
      const url = `${this.basePath}/ingest/${clientId}/crawl_status/${uuid}`;

      console.log('🌐 Fetching crawl status from:', url);

      const response = await httpClient.get<CrawlStatusResponse>(url, { headers });

      console.log('✅ Raw crawl status response:', response.data);

      // Get the LATEST status (last item in array)
      const statusList = response.data.status;
      const latestStatus = statusList.length > 0 ? statusList[statusList.length - 1] : null;

      const parsed: ParsedCrawlStatus = {
        uuid: response.data.uuid,
        serviceType: response.data.serviceType,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
        currentStatus: latestStatus
          ? {
              status: latestStatus.status,
              message: latestStatus.message,
              timestamp: latestStatus.timestamp,
              successfulPages: latestStatus.successfulPages,
              failedPages: latestStatus.failedPages,
              hasSitemap: latestStatus.hasSitemap,
              totalSitemapUrls: latestStatus.totalSitemapUrls,
              urlsCrawled: latestStatus.urlsCrawled,
            }
          : null,
      };

      console.log('✅ Parsed crawl status:', parsed);
      logger.info('Crawl status fetched successfully');
      return parsed;
    } catch (error: any) {
      console.error('❌ Failed to fetch crawl status:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${this.basePath}/ingest/${clientId}/crawl_status/${uuid}`,
      });
      logger.error('Failed to fetch crawl status:', error);
      throw error;
    }
  }
}

export const fileEndpoints = FileEndpoints.getInstance();
